import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import useEditorDraft from '../hooks/useEditorDraft';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import EditorSaveBar from '../components/EditorSaveBar';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { ArticleService } from '../services/articleService';
import { AuthService } from '../services/authService';
import { PlatformService } from '../services/platformService';
import { CategoryService } from '../services/categoryService';
import { DossierService } from '../services/dossierService';
import { UrlValidator } from '../utils/urlValidator';
import { formatTags, parseTags } from '../utils/tagUtils';
import { Article, Platform, Category, Dossier, User } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import PageLoader from '../components/ui/PageLoader';
import { ArticleImageSelector } from '../components/ArticleImageSelector';
import useSourceUrls, { SourceUrl } from '../hooks/useSourceUrls';
import { 
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface ArticleFormData {
  authorId: string;
  topic: string;
  title: string;
  subtitle: string;
  teaser: string;
  metaTitle: string;
  metaDescription: string;
  content: string;
  categoryId: string;
  dossierId: string;
  tags: string;
  imageType: 'eigenes' | 'ki' | 'redaktion';
  imagePrompt: string;
  imageSource: string;
  imageSourceUrl: string;
  imageStorageUrl: string;
  imageCaption: string;
  imageCaptionFromPrompt: string;
  uploadedFile?: File;
  sourceUrls: SourceUrl[];
}

export const EditArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // States
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [article, setArticle] = useState<Article | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [authors, setAuthors] = useState<User[]>([]);
  const [isLoadingAuthors, setIsLoadingAuthors] = useState(false);
  const [authorsError, setAuthorsError] = useState(false);
  const [authorsReload, setAuthorsReload] = useState(0);
  const canChangeAuthor = article?.status === 'draft' && Boolean(
    user?.roles.some(role => role === 'administrator' || role === 'redakteur')
  );
  
  const [formData, setFormData] = useState<ArticleFormData>({
    authorId: '',
    topic: '',
    title: '',
    subtitle: '',
    teaser: '',
    metaTitle: '',
    metaDescription: '',
    content: '',
    categoryId: '',
    dossierId: '',
    tags: '',
    imageType: 'eigenes',
    imagePrompt: '',
    imageSource: '',
    imageSourceUrl: '',
    imageStorageUrl: '',
    imageCaption: '',
    imageCaptionFromPrompt: '',
    uploadedFile: undefined,
    sourceUrls: []
  });

  const completed = React.useRef(false);
  const [baseArticleUpdatedAt, setBaseArticleUpdatedAt] = useState('');
  const draftValue = React.useMemo(() => {
    const { uploadedFile, ...fields } = formData;
    return { formData: { ...fields, sourceUrls: fields.sourceUrls.map(source => ({ ...source, isProcessing: false })) }, baseArticleUpdatedAt };
  }, [formData, baseArticleUpdatedAt]);
  const draft = useEditorDraft({ ownerId: user?.id, editorKey: `edit-${id}`, value: draftValue,
    enabled: !!user && !isLoading && !!article, hasContent: true,
    onRestore: saved => {
      if (!saved.formData || !Array.isArray(saved.formData.sourceUrls)) { toast.error('Bearbeitungsstand kann nicht wiederhergestellt werden.'); return; }
      setFormData(prev => ({ ...prev, ...saved.formData,
        authorId: canChangeAuthor ? (saved.formData.authorId || prev.authorId) : prev.authorId,
        uploadedFile: undefined }));
      setBaseArticleUpdatedAt(saved.baseArticleUpdatedAt);
    }
  });
  const blocker = useUnsavedChanges(() => !completed.current && (draft.dirty || isSaving || isImageLoading || (!!formData.uploadedFile && !formData.imageStorageUrl)), false, isSaving || isImageLoading);

  // Form-Update-Funktion
  const updateFormData = useCallback((field: keyof ArticleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Quellen-URL-Funktionen
  const setSourceUrls = useCallback((updater: SourceUrl[] | ((prev: SourceUrl[]) => SourceUrl[])) => {
    setFormData(prev => {
      const nextSourceUrls = typeof updater === 'function'
        ? (updater as (prev: SourceUrl[]) => SourceUrl[])(prev.sourceUrls)
        : updater;
      return { ...prev, sourceUrls: nextSourceUrls };
    });
  }, []);

  const {
    addSourceUrl,
    updateSourceUrl,
    removeSourceUrl,
    validateAndExtractSourceUrl
  } = useSourceUrls(formData.sourceUrls, setSourceUrls);

  // Daten laden
  useEffect(() => {
    const loadData = async () => {
      if (!id || !user) return;
      
      setIsLoading(true);
      try {
        // Artikel laden
        const articleData = await ArticleService.getArticle(id, user);
        setArticle(articleData);
        setBaseArticleUpdatedAt(articleData.updatedAt.toISOString());
        
        // Plattform laden
        const platformData = await PlatformService.getPlatformById(articleData.platformId);
        setPlatform(platformData);
        
        // Kategorien und Dossiers für die Plattform laden
        const [categoriesData, dossiersData] = await Promise.all([
          CategoryService.getCategoriesByPlatform(articleData.platformId),
          DossierService.getDossiersByPlatform(articleData.platformId)
        ]);
        setCategories(categoriesData);
        setDossiers(dossiersData);
        
        // Quellen-URLs aus article.sources laden (statt aus Content zu extrahieren)
        const loadedSources: SourceUrl[] = [];
        if (articleData.sources && Array.isArray(articleData.sources)) {
          loadedSources.push(...articleData.sources.map(source => ({
            id: source.id,
            url: source.url,
            title: source.title,
            isValid: true,
            isProcessing: false
          })));
        }

        // Debug: Zeige geladene Quellen
        console.log('Geladene Quellen aus article.sources:', loadedSources);
        console.log('Artikel-Sources:', articleData.sources);

        // ImageType automatisch ermitteln (Fallback für ältere Entwürfe)
        let imageType: 'eigenes' | 'ki' | 'redaktion' = articleData.imageType || 'eigenes';
        if (!articleData.imageType) {
          if (articleData.imageSourceUrl?.includes('openai.com')) {
            imageType = 'ki';
          } else if (!articleData.imageStorageUrl || articleData.imageStorageUrl.includes('68e4ee76002eac11ff3e')) {
            imageType = 'redaktion';
          }
        }

        // Formular mit Artikel-Daten füllen
        setFormData({
          authorId: articleData.authorId,
          topic: articleData.topic || '',
          title: articleData.title || '',
          subtitle: articleData.subtitle || '',
          teaser: articleData.teaser || '',
          metaTitle: articleData.metaTitle || '',
          metaDescription: articleData.metaDescription || '',
          content: articleData.content || '',
          categoryId: articleData.categoryId || '',
          dossierId: articleData.dossierId || '',
          tags: formatTags(articleData.tags),
          imageType: imageType,
          imagePrompt: articleData.imagePrompt || '',
          imageSource: articleData.imageSource || '',
          imageSourceUrl: articleData.imageSourceUrl || '',
          imageStorageUrl: articleData.imageStorageUrl || '',
          imageCaption: articleData.imageCaption || '',
          imageCaptionFromPrompt: articleData.imageCaptionFromPrompt || '',
          uploadedFile: undefined,
          sourceUrls: loadedSources
        });
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Fehler beim Laden der Artikel-Daten');
        navigate('/articles');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, user, navigate]);

  useEffect(() => {
    if (!canChangeAuthor) return;
    let cancelled = false;
    setIsLoadingAuthors(true);
    setAuthorsError(false);
    AuthService.getAllUsers()
      .then(users => {
        if (!cancelled) setAuthors(users.sort((a, b) => a.name.localeCompare(b.name, 'de')));
      })
      .catch(() => { if (!cancelled) setAuthorsError(true); })
      .finally(() => { if (!cancelled) setIsLoadingAuthors(false); });
    return () => { cancelled = true; };
  }, [canChangeAuthor, authorsReload]);

  // Berechtigung prüfen
  useEffect(() => {
    if (article && user) {
      const isEditorOrAdmin =
        user.roles.includes('administrator') ||
        user.roles.includes('redakteur');
      const isAuthor = user.id === article.authorId;
      const canEditDraft = ['draft', 'rework_requested'].includes(article.status) && (isAuthor || isEditorOrAdmin);
      const canEditSubmitted = article.status === 'ready' && (isEditorOrAdmin || (isAuthor && article.publicationState === 'failed'));
      const processing = article.publicationRequestId && ['queued', 'sending'].includes(article.publicationState || '');
      const canEdit = !processing && (canEditDraft || canEditSubmitted);
      
      if (!canEdit) {
        if (processing) {
          toast.error('Der Artikel wird im Hintergrund verarbeitet. Änderungen sind nach Abschluss der Übergabe wieder möglich.');
          navigate(`/articles/${article.id}`);
          return;
        }
        toast.error('Keine Berechtigung zum Bearbeiten dieses Artikels');
        navigate(`/articles/${id}`);
      }
    }
  }, [article, user, id, navigate]);

  // Validierung
  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      toast.error('Bitte gib einen Titel ein');
      return false;
    }
    
    if (!formData.content.trim()) {
      toast.error('Bitte gib den Artikel-Inhalt ein');
      return false;
    }
    
    if (!formData.categoryId) {
      toast.error('Bitte wähle eine Kategorie aus');
      return false;
    }
    
    if (!formData.dossierId) {
      toast.error('Bitte wähle ein Dossier aus');
      return false;
    }
    
    // Prüfe Quellen-URLs
    if (formData.sourceUrls.length > 0) {
      const hasInvalidUrls = formData.sourceUrls.some(source => 
        source.url.trim() !== '' && !source.isValid
      );
      
      if (hasInvalidUrls) {
        toast.error('Bitte korrigiere die ungültigen URLs oder entferne sie');
        return false;
      }
    }
    
    // Prüfe Bildquelle-URL wenn vorhanden
    if (formData.imageSourceUrl.trim()) {
      const validation = UrlValidator.validateImageSourceUrl(formData.imageSourceUrl);
      if (!validation.isValid) {
        toast.error(validation.error || 'Bitte gib eine gültige URL für die Bildquelle ein');
        return false;
      }
    }

    // Bildunterschrift ist bei eigenem Bild Pflicht
    if (formData.imageType === 'eigenes' && !formData.imageCaption.trim()) {
      toast.error('Bitte gib eine Bildunterschrift an');
      return false;
    }
    
    return true;
  };

  // Speichern
  const handleSave = async () => {
    if (!article || !user || isSaving || isImageLoading || !draft.ready || draft.recovery || (article.status !== 'draft' && !validateForm())) return;
    
    setIsSaving(true);
    try {
      const current = await ArticleService.getArticle(article.id, user);
      if (current.updatedAt.toISOString() !== baseArticleUpdatedAt) throw new Error('Der Artikel wurde inzwischen geändert. Dein privater Bearbeitungsstand bleibt erhalten. Bitte sichere deinen Text und lade den Artikel neu, um die Fassungen abzugleichen.');
      const tagsArray = parseTags(formData.tags);
      const tagsString = formatTags(tagsArray);
      
      // Quellen separat für sources-Feld extrahieren
      const sourcesArray = formData.sourceUrls
        .filter(source => source.url.trim())
        .map(source => ({
          id: source.id,
          url: source.url,
          title: source.title
        }));
      
      await ArticleService.updateArticle(article.id, {
        ...(canChangeAuthor && formData.authorId !== article.authorId ? { authorId: formData.authorId } : {}),
        topic: formData.topic,
        title: formData.title,
        subtitle: formData.subtitle,
        teaser: formData.teaser,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        content: formData.content, // Content OHNE Quellen speichern
        categoryId: formData.categoryId,
        dossierId: formData.dossierId,
        tags: tagsString,
        imageType: formData.imageType,
        imagePrompt: formData.imagePrompt,
        imageSource: formData.imageSource,
        imageSourceUrl: formData.imageSourceUrl,
        imageStorageUrl: formData.imageStorageUrl,
        imageCaption: formData.imageCaption,
        imageCaptionFromPrompt: formData.imageCaptionFromPrompt,
        sources: sourcesArray // Quellen separat im sources-Feld speichern
      }, user);
      
      completed.current = true;
      try { await draft.complete(); } catch { toast.error('Artikel gespeichert. Der private Bearbeitungsstand konnte noch nicht entfernt werden.'); }
      toast.success('Artikel gespeichert.');
      navigate(`/articles/${article.id}`);
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(userFacingError(error, 'Fehler beim Speichern des Artikels'), { duration: 10000 });
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return <PageLoader />;
  }

  if (!article || !platform) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">Artikel oder Plattform konnte nicht geladen werden.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader title="Artikel bearbeiten" description="Änderungen werden privat gesichert. Mit „Änderungen speichern“ übernimmst du sie in den Artikel." actions={
        <Button variant="outline" onClick={() => navigate(`/articles/${article.id}`)}>
          <ArrowLeftIcon className="w-4 h-4 mr-2" />Zurück zum Artikel
        </Button>
      } />
      <EditorSaveBar {...draft}>
        <Button onClick={handleSave} loading={isSaving} disabled={!draft.ready || !!draft.recovery || isImageLoading}>Änderungen speichern</Button>
      </EditorSaveBar>
      <fieldset disabled={draft.status === 'loading' || !!draft.recovery || isSaving} className="min-w-0">
      {/* Formular */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Inhalt und Metadaten</h2>
        {canChangeAuthor && (
          <div>
            <label htmlFor="article-author" className="block text-sm font-medium text-gray-700 mb-1">Autor</label>
            <select
              id="article-author"
              value={formData.authorId}
              onChange={event => updateFormData('authorId', event.target.value)}
              disabled={isLoadingAuthors || authorsError}
              aria-describedby="article-author-help"
              className="input-field"
            >
              {!authors.some(author => author.id === formData.authorId) && (
                <option value={formData.authorId}>Aktueller Autor (unverändert)</option>
              )}
              {authors.map(author => <option key={author.id} value={author.id}>{author.name}</option>)}
            </select>
            <p id="article-author-help" className="mt-1 text-sm text-gray-500">
              {isLoadingAuthors ? 'Autoren werden geladen …' : 'Die Zuordnung wird mit „Änderungen speichern“ übernommen. Der Entwurf erscheint anschließend beim ausgewählten Autor.'}
            </p>
            {authorsError && (
              <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-sm text-red-700">
                <span>Die Autoren konnten nicht geladen werden.</span>
                <Button variant="outline" size="sm" onClick={() => setAuthorsReload(value => value + 1)}>Erneut versuchen</Button>
              </div>
            )}
          </div>
        )}
        {/* Grunddaten */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Thema"
            value={formData.topic}
            onChange={(e) => updateFormData('topic', e.target.value)}
            placeholder="Thema des Artikels"
          />
          <Input
            label="Titel *"
            value={formData.title}
            onChange={(e) => updateFormData('title', e.target.value)}
            placeholder="Titel des Artikels"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Untertitel"
            value={formData.subtitle}
            onChange={(e) => updateFormData('subtitle', e.target.value)}
            placeholder="Untertitel des Artikels"
          />
          <Textarea
            label="Teaser für Social-Media"
            value={formData.teaser}
            onChange={(e) => updateFormData('teaser', e.target.value)}
            placeholder="Text für Social-Media"
            rows={4}
          />
        </div>

        {/* Artikel-Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Artikel-Text *
          </label>
          <Textarea
            id="article-content"
            aria-label="Artikeltext"
            value={formData.content}
            onChange={(e) => updateFormData('content', e.target.value)}
            placeholder="Artikel-Inhalt hier eingeben..."
            rows={12}
            required
          />
        </div>

        {/* Kategorie und Dossier */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategorie *
            </label>
            <select
              value={formData.categoryId}
              onChange={(e) => updateFormData('categoryId', e.target.value)}
              className="input-field"
              required
            >
              <option value="">Kategorie wählen</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dossier
            </label>
            <select
              value={formData.dossierId}
              onChange={(e) => updateFormData('dossierId', e.target.value)}
              className="input-field"
            >
              <option value="">Dossier wählen</option>
              {dossiers.map(dossier => (
                <option key={dossier.id} value={dossier.id}>
                  {dossier.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Metadaten</h3>
          <p className="text-sm text-gray-500">Fehlen separate Meta-Angaben, werden Artikeltitel und Untertitel als Vorgaben geladen, jeweils auf maximal 100 Zeichen gekürzt. Du kannst sie hier anpassen und speichern.</p>
          <Input label="Meta-Titel" value={formData.metaTitle} onChange={event => updateFormData('metaTitle', event.target.value)} maxLength={100} />
          <Textarea label="Meta-Beschreibung" value={formData.metaDescription} onChange={event => updateFormData('metaDescription', event.target.value)} rows={3} maxLength={100} />
        </div>

        {/* Tags */}
        <Input
          label="Tags"
          value={formData.tags}
          onChange={(e) => updateFormData('tags', e.target.value)}
          placeholder="Tag1, Tag2, Tag3 (durch Komma getrennt)"
        />

        {/* Bild-Sektion */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Artikel-Bild</h3>
          
          <ArticleImageSelector
            imageType={formData.imageType}
            onImageTypeChange={(type) => updateFormData('imageType', type)}
            imageStorageUrl={formData.imageStorageUrl}
            imageSource={formData.imageSource}
            imageSourceUrl={formData.imageSourceUrl}
            imageCaption={formData.imageCaption}
            imageCaptionFromPrompt={formData.imageCaptionFromPrompt}
            imagePrompt={formData.imagePrompt}
            uploadedFile={formData.uploadedFile}
            onImageStorageUrlChange={(url) => updateFormData('imageStorageUrl', url)}
            onImageSourceChange={(source) => updateFormData('imageSource', source)}
            onImageSourceUrlChange={(url) => updateFormData('imageSourceUrl', url)}
            onImageCaptionChange={(caption) => updateFormData('imageCaption', caption)}
            onImagePromptChange={(prompt) => updateFormData('imagePrompt', prompt)}
            onUploadedFileChange={(file) => updateFormData('uploadedFile', file)}
            platform={platform}
            cachedMetadata={undefined}
            onLoadingChange={setIsImageLoading}
          />
          
          {/* Bildunterschrift - Separat nur für Redaktion */}
          {formData.imageType === 'redaktion' && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bildunterschrift
              </label>
              <Input
                value={formData.imageCaption}
                onChange={(e) => updateFormData('imageCaption', e.target.value)}
                placeholder="Beschreibung des Bildes"
              />
            </div>
          )}
        </div>

        {/* Quellen-URLs */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Quellen</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={addSourceUrl}
              className="flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Quelle hinzufügen
            </Button>
          </div>

          {formData.sourceUrls.map((source, index) => (
            <div key={source.id} className="flex flex-wrap items-start gap-2 mb-3">
              <Input
                aria-label={`Quellenadresse ${index + 1}`}
                value={source.url}
                onChange={(e) => updateSourceUrl(source.id, e.target.value)}
                onBlur={() => validateAndExtractSourceUrl(source.id, source.url)}
                placeholder="URL der Quelle"
                className="flex-1"
              />
              {source.isProcessing && (
                <div className="flex items-center px-3">
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {source.isValid && (
                <div className="flex items-center px-3 text-green-600">
                  ✓
                </div>
              )}
              {source.url && !source.isValid && !source.isProcessing && (
                <div className="flex items-center px-3 text-red-600">
                  ✗
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                aria-label={`Quelle ${index + 1} entfernen`}
                onClick={() => removeSourceUrl(source.id)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      </fieldset>
      <ConfirmDialog open={blocker.state === 'blocked'} title="Änderungen noch nicht gesichert"
        description="Die letzte Änderung ist noch nicht gespeichert. Bleibe auf der Seite, bis die Sicherung bestätigt ist, oder verlasse die Seite ohne diese Änderung."
        cancelLabel="Auf Seite bleiben" confirmDisabled={isSaving || isImageLoading} confirmLabel="Ohne letzte Änderung verlassen" danger onClose={() => { if (blocker.state === 'blocked') blocker.reset(); }}
        onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }} />
    </div>
  );
};
